import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { EmptyState, ErrorNotice, LoadingState, PageHeading, Pagination, Panel, SearchInput, SegmentedControl, useDebouncedValue } from '@bcecloud/portal-ui';
import { Checkbox } from '@/components/ui/checkbox';
import { errorMessage } from '@/lib/api';
import { InfoTip, METRIC_INFO } from '@/components/metric-info';
import { readPageKeywords } from '@/lib/pages';
import { RANGES, rangeWindow, type RangeKey } from '@/lib/report-window';
import { useLocalSort } from '@/lib/use-local-sort';
import { QUERY_ACCESSORS, QueryTable } from '@/components/query-table';
import { QueryTrendDrawer } from '@/components/query-trend-drawer';

const RANGE_KEYS: readonly RangeKey[] = ['7d', '30d', '90d', '12m'];
const PAGE_SIZE = 25;

/**
 * Analytics → Keywords: every search Google showed any of our pages for,
 * with clicks, impressions and average position against the previous
 * window. A keyword opens its site-wide trend and the pages that rank for
 * it; a page in that list narrows the trend to that page.
 */
export function KeywordsAnalyticsPage(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const [range, setRange] = useState<RangeKey>('30d');
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [page, setPage] = useState(1);
  const [showHidden, setShowHidden] = useState(false);
  const [selected, setSelected] = useState<{ readonly query: string; readonly path: string | null } | null>(null);
  const debounced = useDebouncedValue(search.trim());
  const window = useMemo(() => rangeWindow(range), [range]);
  const rangeLabel = RANGES[range].label;

  useEffect(() => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (debounced) next.set('q', debounced);
        else next.delete('q');
        return next;
      },
      { replace: true },
    );
    setPage(1);
  }, [debounced, setParams]);

  const keywords = useQuery({
    queryKey: ['page-keywords', window.from, window.to, debounced, showHidden],
    queryFn: ({ signal }) =>
      readPageKeywords(
        { from: window.from, to: window.to, ...(debounced ? { search: debounced } : {}), ...(showHidden ? { includeHidden: true } : {}) },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  const rows = keywords.data?.queries ?? [];
  const { sorted, sort, onSort } = useLocalSort(rows, QUERY_ACCESSORS, { key: 'clicks', direction: 'desc' });
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const shown = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="ANALYTICS"
        title="Keywords"
        subtitle="Every search that showed one of our pages on Google, with clicks, impressions and average position against the previous window. Search Console, live."
      />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <SearchInput label="Search keywords" placeholder="Keyword…" value={search} onChange={(event) => setSearch(event.target.value)} className="w-full max-w-80 min-w-56" />
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-portal-small text-portal-ink">
            <Checkbox checked={showHidden} onCheckedChange={(checked) => { setShowHidden(checked === true); setPage(1); }} />
            Include hidden pages
          </label>
          <SegmentedControl<RangeKey>
            label="Range"
            value={range}
            onChange={(next) => {
              setRange(next);
              setPage(1);
            }}
            options={RANGE_KEYS.map((key) => ({ key, label: RANGES[key].label, shortLabel: RANGES[key].shortLabel }))}
          />
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-portal-micro text-portal-muted">
        <span className="inline-flex items-center gap-1">Clicks <InfoTip label="clicks" text={METRIC_INFO.clicks} /></span>
        <span className="inline-flex items-center gap-1">Impressions <InfoTip label="impressions" text={METRIC_INFO.impressions} /></span>
        <span className="inline-flex items-center gap-1">CTR <InfoTip label="CTR" text={METRIC_INFO.ctr} /></span>
        <span className="inline-flex items-center gap-1">Position <InfoTip label="position" text={METRIC_INFO.position} /></span>
      </div>

      {keywords.isError ? (
        <ErrorNotice title="Keywords unavailable" message={errorMessage(keywords.error)} />
      ) : keywords.isPending ? (
        <LoadingState label="Asking Search Console…" />
      ) : sorted.length === 0 ? (
        <Panel>
          <EmptyState title={debounced ? 'No keywords match' : 'No keywords in this range'} message={debounced ? 'Try a different search.' : 'Google showed none of our pages in this range.'} />
        </Panel>
      ) : (
        <>
          <QueryTable
            rows={shown}
            sort={sort}
            onSort={onSort}
            onSelect={(query) => setSelected({ query, path: null })}
          />
          <Pagination
            page={page}
            totalPages={totalPages}
            onChange={setPage}
            summary={`${(page - 1) * PAGE_SIZE + 1}–${(page - 1) * PAGE_SIZE + shown.length} of ${sorted.length.toLocaleString()} keywords`}
            className="mt-3"
          />
          <p className="mt-2 text-portal-micro text-portal-muted">Change is vs the previous {rangeLabel}. Click a keyword for its trend and the pages that rank for it.</p>
        </>
      )}

      <QueryTrendDrawer
        path={selected?.path ?? null}
        query={selected?.query ?? null}
        window={window}
        rangeLabel={rangeLabel}
        onClose={() => setSelected(null)}
        onSelectPage={(path) => setSelected((current) => (current ? { query: current.query, path } : current))}
        onBack={() => setSelected((current) => (current ? { query: current.query, path: null } : current))}
      />
    </section>
  );
}
